import React, { useEffect, useState, useMemo } from 'react';
import { auth, firestore } from '../firebase';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Bar } from 'recharts';

const Admin = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ images: [], videos: [], users: [] });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [planFilter, setPlanFilter] = useState('all'); // 'all', 'free', 'starter', 'pro', 'business'

  // Resolve auth state once on mount
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  // Check if user is admin
  useEffect(() => {
    let isMounted = true;
    const checkAdmin = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      try {
        const settingsRef = doc(firestore, 'admin', 'settings');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const allowedEmails = settingsSnap.data().allowedEmails || [];
          if (isMounted) {
            setIsAdmin(allowedEmails.includes(currentUser.email));
          }
        } else {
          if (isMounted) setIsAdmin(false);
        }
      } catch (e) {
        console.error('Error checking admin status:', e);
        if (isMounted) {
          setIsAdmin(false);
          setError('Fehler beim Prüfen der Admin-Rechte');
        }
      }
    };
    if (authReady) checkAdmin();
    return () => { isMounted = false; };
  }, [authReady, currentUser]);

  // Fetch all users when admin status is confirmed (directly from Firestore for localhost)
  useEffect(() => {
    let isMounted = true;
    const fetchUsers = async () => {
      if (!isAdmin) {
        setLoading(false);
        return;
      }
      try {
        // Fetch ALL users directly from Firestore (no limit, no orderBy to include users without createdAt)
        const usersRef = collection(firestore, 'users');
        const usersQuery = query(usersRef); // No orderBy to include users without createdAt field
        const usersSnapshot = await getDocs(usersQuery);
        
        const users = [];
        usersSnapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          let createdAtMs = 0;
          let lastLoginMs = 0;
          
          if (userData.createdAt?.toMillis) {
            createdAtMs = userData.createdAt.toMillis();
          }
          if (userData.lastLogin?.toMillis) {
            lastLoginMs = userData.lastLogin.toMillis();
          }
          
          users.push({
            id: userDoc.id,
            email: userData.email,
            balance: userData.balance,
            plan: userData.plan || userData.subscriptionPlan || 'free',
            cancelReasonType: userData.cancel_reason_type ?? null,
            planCanceled: !!userData.cancel_reason_type,
            createdAt: userData.createdAt?.toDate?.()?.toISOString?.(),
            createdAtMs,
            lastLogin: userData.lastLogin?.toDate?.()?.toISOString?.(),
            lastLoginMs,
          });
        });
        
        if (isMounted) {
          // Sort users by createdAtMs desc
          const sortedUsers = users.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
          setData((prev) => ({ ...prev, users: sortedUsers }));
          if (!selectedUserId && sortedUsers.length) setSelectedUserId(sortedUsers[0].id);
        }
      } catch (e) {
        console.error('Error fetching users:', e);
        if (isMounted) setError(e.message || 'Fehler beim Laden der User');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (isAdmin) fetchUsers();
    return () => { isMounted = false; };
  }, [isAdmin, selectedUserId]);

  // Fetch images for selected user
  useEffect(() => {
    let isMounted = true;
    const fetchUserImages = async () => {
      if (!isAdmin || !selectedUserId) return;
      try {
        const imagesRef = collection(firestore, 'users', selectedUserId, 'image');
        const imagesQuery = query(imagesRef, orderBy('createdAt', 'desc'), limit(100));
        const imagesSnapshot = await getDocs(imagesQuery);
        
        const images = [];
        imagesSnapshot.forEach((doc) => {
          const data = doc.data();
          let createdAtMs = 0;
          if (data.createdAt?.toMillis) {
            createdAtMs = data.createdAt.toMillis();
          }
          
          images.push({
            id: doc.id,
            userId: selectedUserId,
            imageUrl: data.imageUrl,
            styleImageUrl: data.styleImageUrl,
            prompt: data.prompt,
            processedImageUrlR2: data.processedImageUrlR2,
            generationStatus: data.generationStatus,
            modelProvider: data.modelProvider,
            resolution: data.resolution || data.quality || data.size,
            error: data.error,
            createdAt: data.createdAt?.toDate?.()?.toISOString?.(),
            createdAtMs,
          });
        });
        
        if (isMounted) {
          setData((prev) => ({ ...prev, images }));
        }
      } catch (e) {
        console.error('Error fetching images:', e);
        if (isMounted) setError(e.message || 'Fehler beim Laden der Bilder');
      }
    };
    fetchUserImages();
    return () => { isMounted = false; };
  }, [isAdmin, selectedUserId]);

  // Fetch motion-control videos for selected user
  useEffect(() => {
    let isMounted = true;
    const fetchUserVideos = async () => {
      if (!isAdmin || !selectedUserId) return;
      try {
        const videosRef = collection(firestore, 'users', selectedUserId, 'video');
        const videosQuery = query(videosRef, orderBy('createdAt', 'desc'), limit(100));
        const videosSnapshot = await getDocs(videosQuery);

        const videos = [];
        videosSnapshot.forEach((d) => {
          const v = d.data();
          let createdAtMs = 0;
          let processedAtMs = 0;
          if (v.createdAt?.toMillis) createdAtMs = v.createdAt.toMillis();
          if (v.processedAt?.toMillis) processedAtMs = v.processedAt.toMillis();

          videos.push({
            id: d.id,
            userId: selectedUserId,
            imageUrl: v.imageUrl,
            motionVideoUrl: v.motionVideoUrl,
            prompt: v.prompt,
            modelTier: v.modelTier,
            modelProvider: v.modelProvider,
            creditCost: v.creditCost,
            generationStatus: v.generationStatus,
            processedVideoUrlR2: v.processedVideoUrlR2,
            generationOutputUrl: v.generationOutputUrl,
            error: v.error,
            requestId: v.requestId,
            source: v.source,
            createdAt: v.createdAt?.toDate?.()?.toISOString?.(),
            createdAtMs,
            processedAtMs,
          });
        });

        if (isMounted) {
          setData((prev) => ({ ...prev, videos }));
        }
      } catch (e) {
        console.error('Error fetching videos:', e);
        if (isMounted) setError(e.message || 'Fehler beim Laden der Videos');
      }
    };
    fetchUserVideos();
    return () => { isMounted = false; };
  }, [isAdmin, selectedUserId]);

  const handleSignIn = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (e) {
      setError(e.message || 'Login fehlgeschlagen');
    }
  };

  // Filter users by plan
  const filteredUsers = useMemo(() => {
    if (planFilter === 'all') {
      return data.users;
    }
    return data.users.filter(user => user.plan === planFilter);
  }, [data.users, planFilter]);

  // Combined chart data: Anmeldungen + Conversion Rate pro Tag
  // Conversion = (Starter+Pro+Business an dem Tag) / (alle Anmeldungen an dem Tag) × 100
  const combinedChartData = useMemo(() => {
    if (!data.users || data.users.length === 0) return [];
    
    const countByDate = {};
    const paidByDate = {};
    
    data.users.forEach(user => {
      if (user.createdAtMs) {
        const date = new Date(user.createdAtMs);
        const dateKey = date.toISOString().split('T')[0];
        countByDate[dateKey] = (countByDate[dateKey] || 0) + 1;
        const plan = (user.plan || user.subscriptionPlan || 'free').toLowerCase();
        if (plan === 'starter' || plan === 'pro' || plan === 'business') {
          paidByDate[dateKey] = (paidByDate[dateKey] || 0) + 1;
        }
      }
    });
    
    const monthsDE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    const formatDate = (date) => `${date.getDate()}. ${monthsDE[date.getMonth()]}`;
    
    const sorted = Object.keys(countByDate).sort();
    const rawData = sorted.map(dateKey => {
      const anmeldungen = countByDate[dateKey] || 0;
      const paidPlans = paidByDate[dateKey] || 0;
      const conversionRate = anmeldungen > 0 ? (paidPlans / anmeldungen * 100) : 0;
      const [year, month, day] = dateKey.split('-');
      const date = new Date(year, month - 1, day);
      return {
        date: dateKey,
        displayDate: formatDate(date),
        count: anmeldungen,
        newPaid: paidPlans,
        conversionRate
      };
    });
    
    // 7-Tage-Tendenz: gewichtet (Summe Paid / Summe Anmeldungen) × 100 — nicht Mittelwert der Tages-Prozente,
    // sonst wirkt z. B. 60 % Tendenz obwohl heute 0 Paid (weil frühere Tage im Fenster hohe Tages-Quoten hatten).
    const windowSize = 7;
    return rawData.map((row, i) => {
      const start = Math.max(0, i - windowSize + 1);
      const window = rawData.slice(start, i + 1);
      const sumAnmeldungen = window.reduce((acc, d) => acc + d.count, 0);
      const sumPaid = window.reduce((acc, d) => acc + d.newPaid, 0);
      const conversionRateTrend = sumAnmeldungen > 0 ? (sumPaid / sumAnmeldungen) * 100 : 0;
      return { ...row, conversionRateTrend };
    });
  }, [data.users]);


  if (loading) return <div style={{ padding: 16 }}>Lade…</div>;
  
  if (!currentUser) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ marginBottom: 12 }}>Bitte einloggen, um die Admin-Übersicht zu sehen.</div>
        <button onClick={handleSignIn}>Mit Google einloggen</button>
        {error && <div style={{ color: 'red', marginTop: 8 }}>Fehler: {error}</div>}
      </div>
    );
  }
  
  if (!isAdmin) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ color: 'red' }}>Keine Admin-Berechtigung für {currentUser.email}</div>
      </div>
    );
  }
  
  if (error) return <div style={{ padding: 16, color: 'red' }}>Fehler: {error}</div>;

  // Images already sorted from server; ensure client sort desc by createdAtMs
  const imagesForUser = (data.images || [])
    .slice()
    .sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));

  const videosForUser = (data.videos || [])
    .slice()
    .sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));

  // Pre-compute a few headline stats for the videos section so the admin can
  // see at a glance how many succeeded / failed / are still running for the
  // selected customer.
  const videoStats = videosForUser.reduce(
    (acc, v) => {
      const s = String(v.generationStatus || '').toLowerCase();
      if (s === 'completed' || s === 'success' || s === 'succeeded' || s === 'ok') acc.completed += 1;
      else if (s === 'failed' || s === 'error') acc.failed += 1;
      else acc.inflight += 1;
      return acc;
    },
    { completed: 0, failed: 0, inflight: 0 }
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100vh' }}>
      {/* Neue User + Conversion Rate (kombiniert) */}
      {combinedChartData.length > 0 && (
        <div style={{ padding: 16, borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Neue User & Conversion Rate</h3>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <div style={{ width: 16, height: 12, backgroundColor: '#4f46e5', borderRadius: 2 }}></div>
                <span>Neue User</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <div style={{ width: 16, height: 2, backgroundColor: '#10b981', borderRadius: 1 }}></div>
                <span>Conversion Rate</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <div style={{ width: 16, borderTop: '3px dashed #dc2626' }}></div>
                <span>Tendenz (7 Tage, gewichtet)</span>
              </div>
              <div style={{ 
                padding: '12px 24px', 
                backgroundColor: '#4f46e5', 
                color: 'white', 
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(79, 70, 229, 0.3)'
              }}>
                <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 4 }}>
                  {planFilter === 'all' ? 'Total User' : `${planFilter.charAt(0).toUpperCase() + planFilter.slice(1)} User`}
                </div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>{filteredUsers.length}</div>
                {planFilter !== 'all' && (
                  <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>von {data.users.length} gesamt</div>
                )}
              </div>
              <div style={{ 
                padding: '8px 16px',
                backgroundColor: '#10b981',
                color: 'white',
                borderRadius: 6,
                fontWeight: 600
              }}>
                Conv.: {combinedChartData[combinedChartData.length - 1]?.conversionRate.toFixed(2)}%
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={combinedChartData} margin={{ top: 5, right: 50, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="displayDate" 
                style={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                yAxisId="left"
                style={{ fontSize: 12 }}
                label={{ value: 'Neue Anmeldungen', angle: -90, position: 'insideLeft' }}
              />
              <YAxis 
                yAxisId="right"
                type="number"
                orientation="right"
                style={{ fontSize: 12 }}
                label={{ value: 'Conversion Rate (%)', angle: 90, position: 'insideRight' }}
                domain={[0, 25]}
                ticks={[0, 5, 10, 15, 20, 25]}
                allowDataOverflow
              />
              <Tooltip 
                formatter={(value, name, props) => {
                  const payload = props?.payload;
                  if (name === 'count') return [`${value}`, 'Anmeldungen'];
                  if (name === 'conversionRate') return [`${value.toFixed(2)}%`, `Conversion (${payload?.newPaid ?? 0} Paid-Pläne / ${payload?.count ?? 0} Anmeldungen)`];
                  if (name === 'conversionRateTrend') return [`${value.toFixed(2)}%`, 'Tendenz (Σ Paid / Σ Anmeldungen, 7 Tage)'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Datum: ${label}`}
              />
              <Bar yAxisId="left" dataKey="count" fill="#4f46e5" radius={[4, 4, 0, 0]} />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="conversionRate" 
                stroke="#10b981" 
                strokeWidth={1.5}
                dot={{ fill: '#10b981', r: 2 }}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="conversionRateTrend" 
                stroke="#dc2626"
                strokeWidth={4}
                strokeDasharray="6 4"
                dot={false}
                isAnimationActive={false}
                zIndex={1500}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      <div style={{ display: 'flex', flex: 1 }}>
        <aside style={{ width: 300, borderRight: '1px solid #eee', padding: 12 }}>
          <h3 style={{ marginTop: 0 }}>User</h3>
          {/* Plan Filter */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 6 }}>Filter nach Plan:</label>
            <select 
              value={planFilter} 
              onChange={(e) => setPlanFilter(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #ddd',
                backgroundColor: 'white',
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              <option value="all">Alle Pläne ({data.users.length})</option>
              <option value="free">Free ({data.users.filter(u => u.plan === 'free').length})</option>
              <option value="starter">Starter ({data.users.filter(u => u.plan === 'starter').length})</option>
              <option value="pro">Pro ({data.users.filter(u => u.plan === 'pro').length})</option>
              <option value="business">Business ({data.users.filter(u => u.plan === 'business').length})</option>
            </select>
          </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(filteredUsers || []).map((u) => (
            <button
              key={u.id}
              onClick={() => setSelectedUserId(u.id)}
              style={{
                textAlign: 'left',
                padding: 8,
                borderRadius: 6,
                border: '1px solid #ddd',
                background: u.id === selectedUserId ? '#f0f6ff' : 'white',
                cursor: 'pointer'
              }}
            >
              <div style={{ fontWeight: 600 }}>{u.email || u.id}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4, marginBottom: 4 }}>
                <div style={{ 
                  fontSize: 11, 
                  fontWeight: 600,
                  color: 'white',
                  backgroundColor: u.plan === 'business' ? '#10b981' : u.plan === 'pro' ? '#6366f1' : u.plan === 'starter' ? '#f59e0b' : '#94a3b8',
                  padding: '2px 8px',
                  borderRadius: 4,
                  textTransform: 'uppercase'
                }}>
                  {u.plan || 'free'}
                </div>
                {(u.plan === 'starter' || u.plan === 'pro' || u.plan === 'business') && (
                  <div style={{ 
                    fontSize: 10, 
                    fontWeight: 600,
                    color: u.planCanceled ? 'white' : '#166534',
                    backgroundColor: u.planCanceled ? '#dc2626' : '#86efac',
                    padding: '2px 6px',
                    borderRadius: 4
                  }}>
                    {u.planCanceled ? 'Canceled' : 'Active'}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>Erstellt: {u.createdAt ? new Date(u.createdAtMs || 0).toLocaleString() : '-'}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Letztes Login: {u.lastLogin ? new Date(u.lastLoginMs || 0).toLocaleString() : '-'}</div>
              <div style={{ fontSize: 12, color: '#333' }}>Credits: {typeof u.balance === 'number' ? u.balance : '-'}</div>
            </button>
          ))}
        </div>
        </aside>
        <main style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
          {/* === Videos (Motion Control) === */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 0, marginBottom: 8 }}>
            <h2 style={{ margin: 0 }}>Videos</h2>
            <span style={{ fontSize: 12, color: '#666' }}>
              {videosForUser.length} total · {videoStats.completed} ✓ · {videoStats.failed} ✗ · {videoStats.inflight} ⏳
            </span>
          </div>
          {videosForUser.length === 0 ? (
            <div style={{ fontSize: 12, color: '#999', marginBottom: 16 }}>Keine Videos für diesen User.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 24 }}>
              {videosForUser.map((v) => {
                const status = String(v.generationStatus || '').toLowerCase();
                const isCompleted = ['completed', 'success', 'succeeded', 'ok'].includes(status);
                const isFailed = ['failed', 'error'].includes(status);
                const statusColor = isCompleted ? '#10b981' : isFailed ? '#dc2626' : '#f59e0b';
                const tier = String(v.modelTier || 'pro').toLowerCase();
                const ts = v.createdAtMs || (v.createdAt ? Date.parse(v.createdAt) : 0);
                return (
                  <div key={`video-${v.userId}-${v.id}`} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 8, background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 4 }}>
                      <div style={{ fontSize: 11, color: '#888', wordBreak: 'break-all' }}>{v.id}</div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <span style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: 'white',
                          background: tier === 'basic' ? '#94a3b8' : '#6366f1',
                          padding: '2px 6px',
                          borderRadius: 4,
                          textTransform: 'uppercase',
                        }}>{tier === 'basic' ? 'Basic' : 'Pro'}</span>
                        <span style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: 'white',
                          background: statusColor,
                          padding: '2px 6px',
                          borderRadius: 4,
                          textTransform: 'uppercase',
                        }}>{v.generationStatus || '-'}</span>
                      </div>
                    </div>

                    <div style={{ fontSize: 11, color: '#666' }}>Erstellt: {ts ? new Date(ts).toLocaleString() : '-'}</div>
                    {typeof v.creditCost === 'number' && (
                      <div style={{ fontSize: 11, color: '#666' }}>Credits: {v.creditCost}</div>
                    )}
                    <div style={{ fontSize: 12, marginTop: 4, color: '#333' }}>
                      Prompt: <span style={{ color: v.prompt ? '#111' : '#999' }}>{v.prompt || '—'}</span>
                    </div>

                    {/* Inputs */}
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {v.imageUrl && (
                        <div>
                          <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Character</div>
                          <img src={v.imageUrl} alt="character input" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                        </div>
                      )}
                      {v.motionVideoUrl && (
                        <div>
                          <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Motion ref</div>
                          <video
                            src={v.motionVideoUrl}
                            muted
                            playsInline
                            controls
                            preload="metadata"
                            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee', background: '#000' }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Output */}
                    {v.processedVideoUrlR2 ? (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Final output</div>
                        <video
                          src={v.processedVideoUrlR2}
                          controls
                          playsInline
                          preload="metadata"
                          style={{ width: '100%', maxHeight: 320, background: '#000', borderRadius: 6 }}
                        />
                        <div style={{ marginTop: 4 }}>
                          <a href={v.processedVideoUrlR2} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>Direkt-Link</a>
                        </div>
                      </div>
                    ) : isFailed ? (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#dc2626' }}>
                        {v.error || 'Generation failed.'}
                      </div>
                    ) : (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                        Noch in Bearbeitung…
                      </div>
                    )}

                    {v.error && v.processedVideoUrlR2 && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#dc2626' }}>Error: {v.error}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* === Bilder === */}
          <h2 style={{ marginTop: 8 }}>Bilder</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {imagesForUser.map((it) => (
            <div key={`img-${it.userId}-${it.id}`} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 8 }}>
              <div style={{ fontSize: 12, color: '#555' }}>{it.userId} / {it.id}</div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Erstellt: {(() => {
                const ts = it.createdAtMs || (it.createdAt ? Date.parse(it.createdAt) : 0);
                return ts ? new Date(ts).toLocaleString() : '-';
              })()}</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Prompt: {it.prompt || '-'}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {it.imageUrl && (
                  <div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Input</div>
                    <img src={it.imageUrl} alt="input" style={{ width: 100, height: 100, objectFit: 'cover' }} />
                  </div>
                )}
                {it.styleImageUrl && (
                  <div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Style</div>
                    <img src={it.styleImageUrl} alt="style" style={{ width: 100, height: 100, objectFit: 'cover' }} />
                  </div>
                )}
                {it.gemini_image_url && (
                  <div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Gemini</div>
                    <img src={it.gemini_image_url} alt="gemini output" style={{ width: 100, height: 100, objectFit: 'cover' }} />
                  </div>
                )}
              </div>
              {it.processedImageUrlR2 && (
                <div style={{ marginTop: 8 }}>
                  <a href={it.processedImageUrlR2} target="_blank" rel="noreferrer">Final Output ansehen</a>
                </div>
              )}
              <div style={{ fontSize: 12, marginTop: 8 }}>Status: {it.generationStatus || '-'}</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Provider: {it.modelProvider || '-'}</div>
              {it.resolution && <div style={{ fontSize: 12, marginTop: 4, fontWeight: 600, color: '#4f46e5' }}>Auflösung: {it.resolution}</div>}
              {it.error && <div style={{ fontSize: 12, color: 'red' }}>Error: {it.error}</div>}
            </div>
          ))}
        </div>
        </main>
      </div>
    </div>
  );
};

export default Admin;


